/**
 * PageTransition — AnimatePresence wrapper for route-level transitions.
 *
 * Wrap the router outlet with this component and pass the current path as `pathKey`.
 *
 * Usage (wouter):
 *   const [loc] = useLocation();
 *   <PageTransition pathKey={loc} variant="slide">
 *     <Route path="/" component={Home} />
 *   </PageTransition>
 *
 * Usage (react-router):
 *   <PageTransition pathKey={location.pathname}>
 *     <Routes>...</Routes>
 *   </PageTransition>
 *
 * Variants:
 *  "fade"  — simple opacity cross-fade (default)
 *  "slide" — slides right→left on enter, left→right on exit (standard mobile)
 *  "up"    — slides up on enter (modal-style, good for detail pages)
 *  "scale" — scales up slightly from center (card expand feel)
 */
'use client';

import { AnimatePresence, motion, type TargetAndTransition, type Transition } from 'framer-motion';
import type { ReactNode } from 'react';

type Variant = 'fade' | 'slide' | 'up' | 'scale';

const variants: Record<
  Variant,
  {
    initial: TargetAndTransition;
    animate: TargetAndTransition;
    exit: TargetAndTransition;
    transition: Transition;
  }
> = {
  fade: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { duration: 0.18, ease: 'easeOut' },
  },
  slide: {
    initial: { opacity: 0, x: 24 },
    animate: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -16 },
    transition: { duration: 0.22, ease: [0.4, 0, 0.2, 1] },
  },
  up: {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -12 },
    transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] },
  },
  scale: {
    initial: { opacity: 0, scale: 0.96 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 1.02 },
    transition: { duration: 0.2, ease: [0.4, 0, 0.2, 1] },
  },
};

interface PageTransitionProps {
  pathKey: string;
  variant?: Variant;
  children: ReactNode;
  className?: string;
}

export function PageTransition({
  pathKey,
  variant = 'fade',
  children,
  className,
}: PageTransitionProps) {
  const v = variants[variant];
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={pathKey}
        initial={v.initial}
        animate={v.animate}
        exit={v.exit}
        transition={v.transition}
        className={className ?? 'w-full'}
        style={{ willChange: 'opacity, transform' }}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  );
}

/**
 * FadeIn — simple fade-in wrapper for sections, cards, or page segments.
 * Triggers when the element enters the viewport.
 */
interface FadeInProps {
  children: ReactNode;
  delay?: number;
  className?: string;
  once?: boolean;
}

export function FadeIn({ children, delay = 0, className, once = true }: FadeInProps) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once }}
      transition={{ duration: 0.3, delay, ease: 'easeOut' }}
      className={className}
    >
      {children}
    </motion.div>
  );
}
